import { useCallback, useEffect, useState } from "react";
import { KeyRound, Loader2, Mail, MessageCircle, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Chip, Loading, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

const OK = "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
const WARN = "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";

const PLATFORM_LABEL = {
  resend_api_key: "Resend API key",
  mappstore_api_key: "Mappstore API key",
  mappstore_base_url: "Mappstore base URL",
  mappstore_template_name: "Mappstore template name",
};

export const MessagingSettings = () => {
  const [meta, setMeta] = useState(null);
  const [tenant, setTenant] = useState({});
  const [platform, setPlatform] = useState({});
  const [busy, setBusy] = useState("");
  const [testTo, setTestTo] = useState("");
  const [outbox, setOutbox] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/admin/messaging");
    setMeta(res.data);
    setTenant(res.data.tenant);
    setPlatform(Object.fromEntries(res.data.platform_fields.map((f) => [f, ""])));
    try {
      const box = await api.get("/email-outbox");
      setOutbox(box.data);
    } catch { setOutbox(null); }
  }, []);

  const sendTest = async () => {
    setBusy("test");
    try {
      const res = await api.post("/email-outbox/test", { to: testTo });
      toast.success(`Sent to ${res.data.to}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const flushQueue = async () => {
    setBusy("flush");
    try {
      const res = await api.post("/email-outbox/flush");
      toast.success(`${res.data.sent} sent · ${res.data.pending} still waiting`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  useEffect(() => { load().catch(() => setMeta(false)); }, [load]);

  const saveTenant = async () => {
    setBusy("tenant");
    try {
      await api.put("/admin/messaging/tenant", { values: tenant });
      toast.success("Sender identity saved");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const savePlatform = async () => {
    setBusy("platform");
    try {
      const values = Object.fromEntries(Object.entries(platform).filter(([, v]) => v !== ""));
      await api.put("/admin/messaging/platform", { values });
      toast.success("Provider credentials stored encrypted");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!meta) return <Loading label="Loading messaging settings" />;

  return (
    <div className="space-y-4">
      <Panel testId="panel-messaging-status" kicker="Delivery" title="Invite and receipt delivery"
             actions={meta.status.email && (
               <div className="flex flex-wrap items-center gap-2">
                 <Input value={testTo} placeholder="you@example.org"
                        data-testid="messaging-test-to"
                        onChange={(e) => setTestTo(e.target.value)}
                        className="h-8 w-52 bg-muted/40 font-mono text-[12px]" />
                 <Button size="sm" variant="outline" className="h-8 rounded-full"
                         data-testid="messaging-send-test" disabled={busy === "test"}
                         onClick={sendTest}>
                   {busy === "test" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                     : <Mail className="mr-1 h-3.5 w-3.5" />} Send a test
                 </Button>
                 <Button size="sm" variant="ghost" className="h-8 rounded-full"
                         data-testid="messaging-flush-outbox" disabled={busy === "flush"}
                         onClick={flushQueue}>
                   Send what is queued
                 </Button>
               </div>
             )}>
        <div className="flex flex-wrap items-center gap-4 p-4">
          <div className="flex items-center gap-2" data-testid="messaging-email-status">
            <Mail className="h-4 w-4 text-primary" />
            <span className="text-[12.5px] text-foreground">Email (Resend)</span>
            <Chip className={meta.status.email ? OK : WARN}>
              {meta.status.email ? "live" : "link fallback"}
            </Chip>
          </div>
          <div className="flex items-center gap-2" data-testid="messaging-whatsapp-status">
            <MessageCircle className="h-4 w-4 text-primary" />
            <span className="text-[12.5px] text-foreground">WhatsApp (Mappstore)</span>
            <Chip className={meta.status.whatsapp ? OK : WARN}>
              {meta.status.whatsapp ? "live" : "share link fallback"}
            </Chip>
          </div>
          <p className="basis-full text-[11px] text-muted-foreground">
            While a provider is not live, invitations still work — the platform produces a copy link,
            a WhatsApp share and an email draft instead of sending on your behalf.
          </p>
          {outbox && (
            <p className="basis-full text-[11px] text-muted-foreground"
               data-testid="messaging-outbox-line">
              {outbox.configured
                ? `${outbox.pending} email(s) waiting and ${outbox.sent} sent so far.`
                : `${outbox.pending} email(s) — governance notices, the year-end reminder and
                   committee alerts — are waiting safely in the outbox. Missing
                   ${(outbox.missing || []).join(", ").replace(/_/g, " ") || "provider settings"}:
                   store the Resend API key below and set your from-address above, then send a test
                   and release the queue.`}
            </p>
          )}
        </div>
      </Panel>

      <Panel testId="panel-messaging-tenant" kicker="Tenant Admin"
             title="Your sender identity"
             actions={meta.tenant_editable && (
               <Button size="sm" onClick={saveTenant} disabled={busy === "tenant"}
                       data-testid="messaging-tenant-save"
                       className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                 {busy === "tenant" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                   : <Save className="mr-1 h-3.5 w-3.5" />} Save identity
               </Button>
             )}>
        <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
          {meta.tenant_fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">{f.label}</Label>
              <Input value={tenant[f.key] || ""} placeholder={f.placeholder}
                     disabled={!meta.tenant_editable} data-testid={`messaging-${f.key}-input`}
                     onChange={(e) => setTenant({ ...tenant, [f.key]: e.target.value })}
                     className="bg-muted/40 font-mono text-[12px]" />
              {f.help && <p className="text-[10.5px] text-muted-foreground/80">{f.help}</p>}
            </div>
          ))}
        </div>
        <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
          Your charity's own domain, from-address and WhatsApp business number, so donors always see
          your name — not ours.
        </div>
      </Panel>

      <Panel testId="panel-messaging-platform" kicker="Platform Master Admin"
             title="Provider API credentials"
             actions={meta.platform.configurable && (
               <Button size="sm" onClick={savePlatform} disabled={busy === "platform"}
                       data-testid="messaging-platform-save"
                       className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                 {busy === "platform" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                   : <KeyRound className="mr-1 h-3.5 w-3.5" />} Store credentials
               </Button>
             )}>
        {!meta.platform.configurable && (
          <div className="border-b border-border bg-muted/30 px-4 py-2.5 text-[11.5px] text-muted-foreground"
               data-testid="messaging-platform-readonly">
            Provider API keys are held by the Platform Master Admin and are never shown in clear.
            Ask them to connect a provider; you control the sender identity above.
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
          {meta.platform_fields.map((key) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">{PLATFORM_LABEL[key] || key}</Label>
              <Input value={platform[key] || ""}
                     placeholder={meta.platform.values[key] || "not set"}
                     disabled={!meta.platform.configurable}
                     data-testid={`messaging-${key}-input`}
                     onChange={(e) => setPlatform({ ...platform, [key]: e.target.value })}
                     className="bg-muted/40 font-mono text-[12px]" />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1.5 border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground"
             data-testid="messaging-platform-audit">
          <ShieldCheck className="h-3 w-3" />
          {meta.platform.updated_by
            ? `Last set by ${meta.platform.updated_by} · ${dateTime(meta.platform.updated_at)}`
            : "Nothing stored yet — the platform falls back to environment variables."}
          {" "}Keys are encrypted at rest and only the last four characters are ever displayed.
        </div>
      </Panel>
    </div>
  );
};
